/**
 * HexCell — single hex slot in the Board SVG.
 *
 * Originally derived from the (removed) board-game-playground for visual parity. UI is NOT shared (CLAUDE.md).
 * data-testid uses "cascadia-hex-{q},{r}" per the E2E guide requirements.
 * data-player attribute set when wildlife is placed.
 */

import type {
  AxialCoord,
  BoardCell,
  Habitat,
  TileDef,
  TileRotation,
  Wildlife,
} from "@cloudnest/cascadia-engine";
import { axialToPixel } from "../utils/svg-hex";
import {
  Footprint,
  habitatsOf,
  KeystoneMarker,
  TileFill,
} from "../utils/tile-render";
import { PlaceholderWildlife } from "./placeholders";

export interface HexCellProps {
  readonly coord: AxialCoord;
  readonly cell: BoardCell | null;
  /** Resolved engine tile def for the cell (habitats / wildlifeOptions / keystone). */
  readonly tileDef?: TileDef;
  readonly size: number;
  readonly isValidTarget: boolean;
  readonly isGhost?: boolean;
  readonly isIllegal?: boolean;
  /** When `isGhost`, the drafted tile to render in the preview (live rotates). */
  readonly ghostTileDef?: TileDef;
  /** Current in-hand rotation 0..5 — applied to ghost preview. */
  readonly ghostRotation?: TileRotation;
  readonly draftedWildlife?: Wildlife | null;
  readonly onTap: (coord: AxialCoord) => void;
}

const FALLBACK_HABITATS: readonly Habitat[] = ["forest"];

export function HexCell({
  coord,
  cell,
  tileDef,
  size,
  isValidTarget,
  isGhost = false,
  isIllegal = false,
  ghostTileDef,
  ghostRotation = 0,
  draftedWildlife,
  onTap,
}: HexCellProps) {
  const { x, y } = axialToPixel(coord, size);
  const testId = `cascadia-hex-${coord.q},${coord.r}`;

  const corners = Array.from({ length: 6 }, (_, i) => {
    const angleDeg = 60 * i - 30;
    const rad = (Math.PI / 180) * angleDeg;
    return `${(x + size * Math.cos(rad)).toFixed(2)},${(y + size * Math.sin(rad)).toFixed(2)}`;
  }).join(" ");

  const habitats = tileDef ? habitatsOf(tileDef) : FALLBACK_HABITATS;
  const wildlifeOptions = tileDef?.wildlifeOptions ?? [];
  const isKeystone = tileDef?.keystone ?? false;

  const ariaLabel = cell
    ? `Tile at ${coord.q},${coord.r}${cell.wildlife ? `, ${cell.wildlife}` : ", empty"}`
    : `Empty hex at ${coord.q},${coord.r}`;

  return (
    <g
      data-testid={testId}
      data-coord-q={coord.q}
      data-coord-r={coord.r}
      data-player={cell?.wildlife ? "occupied" : undefined}
      data-rotation={cell !== null ? cell.rotation : undefined}
      data-pending={isGhost ? "true" : undefined}
      data-ghost-rotation={
        isGhost && ghostTileDef ? String(ghostRotation) : undefined
      }
      onClick={() => onTap(coord)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onTap(coord);
      }}
      style={{ cursor: isValidTarget || cell !== null ? "pointer" : "default" }}
      aria-label={ariaLabel}
      tabIndex={isValidTarget ? 0 : -1}
    >
      <polygon
        points={corners}
        fill="transparent"
        stroke="none"
        style={{
          pointerEvents:
            isValidTarget || cell !== null || isGhost ? "all" : "none",
        }}
      />

      {cell !== null ? (
        // Occupied cell: render the tile per the approved wireframe design
        <g data-testid={`${testId}-tile`}>
          <TileFill
            cx={x}
            cy={y}
            size={size}
            habitats={habitats}
            rotation={cell.rotation}
          />
          {!cell.wildlife ? (
            <Footprint cx={x} cy={y} size={size} options={wildlifeOptions} />
          ) : null}
          {isKeystone ? <KeystoneMarker cx={x} cy={y} size={size} /> : null}
          {cell.landmark !== null ? (
            <circle
              cx={x}
              cy={y}
              r={size * 0.45}
              fill="none"
              stroke="var(--cn-cascadia-landmark-bronze)"
              strokeWidth={size * 0.05}
              style={{ pointerEvents: "none" }}
            />
          ) : null}
        </g>
      ) : isGhost && ghostTileDef ? (
        // Live placement preview — render the actual drafted tile so the
        // player sees its habitats + rotation before Confirm. Translucent
        // overlay so the underlying valid-dest ring stays visible.
        <g
          data-testid={`${testId}-ghost-tile`}
          opacity={0.55}
          style={{ pointerEvents: "none" }}
        >
          <TileFill
            cx={x}
            cy={y}
            size={size}
            habitats={habitatsOf(ghostTileDef)}
            rotation={ghostRotation}
          />
          {ghostTileDef.wildlifeOptions.length > 0 ? (
            <Footprint
              cx={x}
              cy={y}
              size={size}
              options={ghostTileDef.wildlifeOptions}
            />
          ) : null}
          {ghostTileDef.keystone ? (
            <KeystoneMarker cx={x} cy={y} size={size} />
          ) : null}
        </g>
      ) : (
        <polygon
          points={corners}
          fill={
            isIllegal
              ? "var(--cn-cascadia-illegal-bg)"
              : isValidTarget
                ? "rgba(16, 185, 129, 0.08)"
                : "transparent"
          }
          stroke={
            isIllegal
              ? "var(--cn-cascadia-illegal)"
              : isValidTarget
                ? "var(--cn-cascadia-valid-dest)"
                : isGhost
                  ? "var(--cn-cascadia-ghost-outline)"
                  : "var(--cn-cascadia-tray-empty-slot)"
          }
          strokeWidth={isValidTarget || isGhost || isIllegal ? 2 : 1}
          strokeDasharray={isGhost ? "4 2" : "none"}
        />
      )}

      {cell?.wildlife ? (
        <PlaceholderWildlife
          x={x}
          y={y}
          radius={size * 0.28}
          species={cell.wildlife}
          testId={`${testId}-wildlife`}
        />
      ) : null}

      {!cell?.wildlife && draftedWildlife && cell !== null ? (
        <PlaceholderWildlife
          x={x}
          y={y}
          radius={size * 0.28}
          species={draftedWildlife}
          isDraftable
          testId={`${testId}-wildlife-ghost`}
        />
      ) : null}

      {isValidTarget ? (
        <polygon
          points={corners}
          fill="none"
          stroke="var(--cn-cascadia-valid-dest)"
          strokeWidth={2}
          style={{ pointerEvents: "none" }}
        />
      ) : null}

      {isIllegal ? (
        <polygon
          points={corners}
          fill="var(--cn-cascadia-illegal-bg)"
          stroke="var(--cn-cascadia-illegal)"
          strokeWidth={2}
          style={{ pointerEvents: "none" }}
        />
      ) : null}
    </g>
  );
}
